import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "@reach/router";
import { ChevronDown } from "lucide-react";
import SparkleIcon from "../common/SparkleIcon";

/** Must match gatsby-source-strapi `apiURL` default when no env is set */
const DEFAULT_STRAPI_PUBLIC =
  "https://www.newdelhiairport.in/strapi";

const trimTrailingSlashes = (value = "") =>
  String(value || "").replace(/\/+$/, "");

function getAiSummaryStrapiBaseUrl() {
  return trimTrailingSlashes(DEFAULT_STRAPI_PUBLIC);
}

const normalizePath = (value = "") => {
  const input = String(value || "").trim();
  if (!input) return "";

  try {
    const parsed = new URL(input, "https://placeholder.local");
    let path = parsed.pathname || "/";
    path = path.replace(/\/{2,}/g, "/").trim();
    if (!path.startsWith("/")) path = `/${path}`;
    if (path.length > 1 && !path.endsWith("/")) path = `${path}/`;
    return path;
  } catch (err) {
    return "";
  }
};

const splitToPoints = (text = "") =>
  String(text)
    .split(/(?<=[.!?])\s+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 3);

/** Prefer newline / bullet structure from Strapi; fallback to sentence chunks */
const parseBulletLines = (text = "") => {
  const raw = String(text || "").trim();
  if (!raw) return [];

  const cleanLine = (l) =>
    l
      .replace(/^[•\-*\u2022]\s*/, "")
      .replace(/^\d+\.\s*/, "")
      .trim();

  const lines = raw
    .split(/\n+/)
    .map(cleanLine)
    .filter((l) => l.length > 15);

  if (lines.length >= 1) return lines.slice(0, 5);

  if (raw.includes("•")) {
    const parts = raw
      .split(/\s*•\s*/)
      .map(cleanLine)
      .filter((l) => l.length > 15);
    if (parts.length >= 1) return parts.slice(0, 5);
  }

  const numbered = raw
    .split(/\s*(?=\d+\.\s)/)
    .map(cleanLine)
    .filter((l) => l.length > 15);
  if (numbered.length >= 1) return numbered.slice(0, 5);

  return splitToPoints(raw).slice(0, 5);
};

const TYPING_MS = 14;

const AiSummaryWidget = () => {
  const location = useLocation();
  const strapiBaseUrl = useMemo(() => getAiSummaryStrapiBaseUrl(), []);
  const [summaryData, setSummaryData] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [typedText, setTypedText] = useState("");
  const typingTimer = useRef(null);

  const normalizedPath = useMemo(
    () => normalizePath(location?.pathname || ""),
    [location?.pathname]
  );

  const bullets = useMemo(
    () => parseBulletLines(summaryData?.summary || ""),
    [summaryData?.summary]
  );

  const fullTypedSource = useMemo(() => {
    if (!bullets.length) return "";
    return bullets.map((b) => `• ${b}`).join("\n\n");
  }, [bullets]);

  useEffect(() => {
    if (!normalizedPath) {
      setSummaryData(null);
      setIsLoaded(true);
      return;
    }

    let active = true;
    const controller = new AbortController();

    const run = async () => {
      setIsLoaded(false);
      try {
        const endpoint = `${strapiBaseUrl}/api/ai-summaries/by-url?url=${encodeURIComponent(
          normalizedPath
        )}`;
        const response = await fetch(endpoint, { signal: controller.signal });
        if (!response.ok) throw new Error("AI summary fetch failed");

        const json = await response.json();
        if (!active) return;
        setSummaryData(json?.data || null);
      } catch (err) {
        if (!active || err.name === "AbortError") return;
        setSummaryData(null);
      } finally {
        if (active) setIsLoaded(true);
      }
    };

    run();

    return () => {
      active = false;
      controller.abort();
    };
  }, [normalizedPath, strapiBaseUrl]);

  useEffect(() => {
    setIsOpen(false);
    setTypedText("");
  }, [normalizedPath, summaryData?.summary]);

  useEffect(() => {
    if (typingTimer.current) {
      clearInterval(typingTimer.current);
      typingTimer.current = null;
    }
    if (!isOpen || !fullTypedSource) {
      if (!isOpen) setTypedText("");
      return;
    }

    let i = 0;
    setTypedText("");
    typingTimer.current = setInterval(() => {
      i += 1;
      setTypedText(fullTypedSource.slice(0, i));
      if (i >= fullTypedSource.length) {
        if (typingTimer.current) clearInterval(typingTimer.current);
        typingTimer.current = null;
      }
    }, TYPING_MS);

    return () => {
      if (typingTimer.current) {
        clearInterval(typingTimer.current);
        typingTimer.current = null;
      }
    };
  }, [isOpen, fullTypedSource]);

  const typedSegments = useMemo(() => {
    if (!typedText.trim()) return [];
    return typedText.split(/\n\n/).filter((s) => s.length > 0);
  }, [typedText]);

  const isTyping =
    isOpen && typedText.length < fullTypedSource.length && fullTypedSource.length > 0;

  const summaryText = summaryData?.summary;
  if (!isLoaded || !summaryText || !bullets.length) return null;

  return (
    <section
      className="w-full bg-white"
      aria-label="AI-generated quick read summary"
    >
      <div className="mx-auto px-4 md:px-6 pt-[5vw] md:pt-[4vw] pb-2 lg:pt-[2vw] md:pb-3">
        <div className="mx-auto flex lg:w-[65%] w-full justify-center">
          <div
            className="w-full lg:rounded-[1vw] md:rounded-[2vw] rounded-[3vw] lg:p-[0.15vw] md:p-[0.3vw] p-[0.5vw] shadow-sm animate-quick-read-gradient"
            style={{
              backgroundImage:
                "linear-gradient(120deg, #002C62 0%, #0d4d8c 40%, #1565a8 80%, #002C62 100%)",
            }}
          >
          <div className="overflow-hidden lg:rounded-[1vw] md:rounded-[2vw] rounded-[3vw] bg-white">
            <button
              type="button"
              id="quick-read-toggle"
              aria-expanded={isOpen}
              aria-controls="quick-read-panel"
              onClick={() => setIsOpen((o) => !o)}
              className="flex w-full flex-nowrap items-center justify-center gap-2 px-3 py-[3vw] text-center transition-colors hover:bg-slate-50/90 sm:gap-2.5 md:px-4 lg:py-[1vw] md:py-[2vw]"
            >
              <SparkleIcon className="h-[5vw] w-[5vw] md:w-[3.5vw] md:h-[3.5vw] lg:w-[2.5vw] lg:h-[2.5vw] shrink-0 text-primary" />
              <span className="min-w-0 truncate text-center font-[RedHatRegular] leading-snug text-primary lg:text-[1.25vw] text-[4vw] md:text-[3.6vw]">
                <span className="font-[RedHatBold]">Quick Read</span>
                <span className="mx-1.5 sm:mx-2" aria-hidden>
                  ·
                </span>
                <span>AI-generated summary content.</span>
              </span>
              <ChevronDown
                className={`h-[4vw] w-[4vw] md:w-[3vw] md:h-[3vw] lg:w-[2vw] lg:h-[2vw] shrink-0 text-primary transition-transform duration-300 ${
                  isOpen ? "rotate-180" : "rotate-0"
                }`}
                aria-hidden
              />
            </button>

            <div
              id="quick-read-panel"
              role="region"
              aria-labelledby="quick-read-toggle"
              className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              }`}
            >
              <div className="min-h-0 overflow-hidden">
                <div className="border-t border-slate-100 pb-4 pt-1 lg:px-[2vw] md:px-[3vw] px-[4vw] md:pb-5">
                  <ul
                    className="list-none lg:space-y-[1vw] md:space-y-[2vw] space-y-[3vw] font-[RedHatRegular] lg:text-[1.25vw] text-[4vw] md:text-[3.6vw] leading-tight text-primary"
                    style={{ minHeight: isOpen ? "4.5rem" : undefined }}
                  >
                    {typedSegments.map((segment, idx) => {
                      const line = segment.replace(/^\s*•\s*/, "").trim();
                      const showCaret =
                        isTyping && idx === typedSegments.length - 1;
                      return (
                        <li
                          key={idx}
                          className="flex lg:gap-[1vw] md:gap-[2vw] gap-[3vw] [word-break:break-word]"
                        >
                          <span
                            className="mt-[0.42em] lg:h-[0.5vw] lg:w-[0.5vw] md:h-[1vw] md:w-[1vw] h-[1.5vw] w-[1.5vw] shrink-0 rounded-full bg-primary"
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1 pl-0.5">
                            {line}
                            {showCaret ? (
                              <span
                                className="ml-0.5 inline-block h-[1.05em] w-0.5 translate-y-[0.12em] bg-primary align-middle animate-pulse"
                                aria-hidden
                              />
                            ) : null}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
};

export default AiSummaryWidget;
